/**
 * Navigation Redux Slice
 * 
 * Manages navigation state including active tabs and mobile menu visibility.
 * Handles UI state for the application's navigation components and ensures
 * consistent navigation behavior across the application.
 */

import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { NavigationTab } from '../types';

/**
 * Navigation State Interface
 * 
 * Defines the state structure for navigation-related UI state
 */
interface NavigationState {
  activeTab: NavigationTab; // Currently active navigation tab
  isMenuOpen: boolean;      // Mobile menu visibility state
}

/**
 * Initial navigation state
 * 
 * Sets default active tab and menu state
 */
const initialState: NavigationState = {
  activeTab: 'self-appraisal', // Default to self-appraisal tab
  isMenuOpen: false,           // Menu starts closed
};

/**
 * Navigation slice with reducers for managing UI navigation state
 */
const navigationSlice = createSlice({
  name: 'navigation',
  initialState,
  reducers: {
    /**
     * Set the active navigation tab
     * 
     * @param action - Contains the tab name to activate
     */
    setActiveTab: (state, action: PayloadAction<NavigationTab>) => {
      state.activeTab = action.payload;
    },
    
    /**
     * Toggle mobile menu visibility
     * 
     * Switches between open and closed states for mobile navigation menu
     */
    toggleMenu: (state) => {
      state.isMenuOpen = !state.isMenuOpen;
    },
    
    /**
     * Close mobile menu
     * 
     * Explicitly closes the mobile navigation menu
     */
    closeMenu: (state) => {
      state.isMenuOpen = false;
    },
  },
});

// Export action creators for use in components
export const { setActiveTab, toggleMenu, closeMenu } = navigationSlice.actions;

// Export reducer for store configuration
export default navigationSlice.reducer;
